export interface BucketStat {
	timestamp: number;
	valueCount: number;
	valueSum: number;
	minValue: number;
	maxValue: number;
	lastRealValue: number;
}

export interface RangeStats {
	count: number;
	avg: number;
	pmin: { x: number; y: number };
	pmax: { x: number; y: number };
	hasValues: boolean;
}

export const EMPTY_RANGE_STATS: RangeStats = {
	count: 0,
	avg: 0,
	pmin: { x: 0, y: 0 },
	pmax: { x: 0, y: 0 },
	hasValues: false,
};

export const accumulateRangeStats = (
	stats: readonly BucketStat[] | undefined,
	fromIndex: number,
	toIndex: number,
): RangeStats => {
	if (!stats || stats.length === 0) {
		return EMPTY_RANGE_STATS;
	}

	const start = Math.max(fromIndex, 0);
	const end = Math.min(toIndex, stats.length - 1);

	let valueSum = 0;
	let valueCount = 0;
	let seeded = false;
	let pmin = { x: 0, y: 0 };
	let pmax = { x: 0, y: 0 };

	for (let i = start; i <= end; i++) {
		const bucket = stats[i];
		if (!bucket || bucket.valueCount <= 0) {
			continue;
		}

		valueSum += bucket.valueSum;
		valueCount += bucket.valueCount;

		if (!seeded) {
			pmin = { x: bucket.timestamp, y: bucket.minValue };
			pmax = { x: bucket.timestamp, y: bucket.maxValue };
			seeded = true;
			continue;
		}

		if (bucket.minValue < pmin.y) {
			pmin = { x: bucket.timestamp, y: bucket.minValue };
		}
		if (bucket.maxValue > pmax.y) {
			pmax = { x: bucket.timestamp, y: bucket.maxValue };
		}
	}

	if (!seeded) {
		return EMPTY_RANGE_STATS;
	}

	return {
		count: valueCount,
		avg: valueSum / valueCount,
		pmin,
		pmax,
		hasValues: true,
	};
};

export const accumulateFromValues = (
	data: readonly { timestamp: string | number; value: number | null }[],
	fromIndex: number,
	toIndex: number,
): RangeStats => {
	const start = Math.max(fromIndex, 0);
	const end = Math.min(toIndex, data.length - 1);

	let valueSum = 0;
	let valueCount = 0;
	let seeded = false;
	let pmin = { x: 0, y: 0 };
	let pmax = { x: 0, y: 0 };

	for (let i = start; i <= end; i++) {
		const point = data[i];
		if (!point || point.value === null || point.value === undefined) {
			continue;
		}

		const time =
			typeof point.timestamp === "string"
				? new Date(point.timestamp).getTime()
				: point.timestamp;

		valueSum += point.value;
		valueCount++;

		if (!seeded) {
			pmin = { x: time, y: point.value };
			pmax = { x: time, y: point.value };
			seeded = true;
			continue;
		}

		if (point.value < pmin.y) {
			pmin = { x: time, y: point.value };
		}
		if (point.value > pmax.y) {
			pmax = { x: time, y: point.value };
		}
	}

	if (!seeded) {
		return EMPTY_RANGE_STATS;
	}

	return {
		count: valueCount,
		avg: valueSum / valueCount,
		pmin,
		pmax,
		hasValues: true,
	};
};

export const readLastRealValue = (
	stats: readonly BucketStat[] | undefined,
	index: number,
	fallback: number,
): number => {
	if (!stats || index < 0 || index >= stats.length) {
		return fallback;
	}

	const value = stats[index]?.lastRealValue;
	return value === null || value === undefined ? fallback : value;
};
